import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Loader2, Building2, CheckCircle, XCircle, Clock, Stethoscope, LogOut
} from 'lucide-react';
import api from '../../utils/api';

function doctorHeaders() {
  return {};
}

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  revoked: 'bg-slate-100 text-slate-500 border-slate-200',
};

export default function DoctorAffiliations() {
  const navigate = useNavigate();
  const [affiliations, setAffiliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchAffiliations = useCallback(async () => {
    try {
      const { data } = await api.get('/doctors/me/affiliations', doctorHeaders());
      if (data.success) setAffiliations(data.affiliations || []);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/doctor/login');
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAffiliations();
  }, [fetchAffiliations, navigate]);

  const handleAccept = async (affId) => {
    setActionLoading(affId);
    try {
      await api.post(`/doctors/affiliations/${affId}/accept`, {}, doctorHeaders());
      await fetchAffiliations();
    } catch {} finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (affId) => {
    setActionLoading(affId);
    try {
      await api.post(`/doctors/affiliations/${affId}/reject`, {}, doctorHeaders());
      await fetchAffiliations();
    } catch {} finally {
      setActionLoading(null);
    }
  };

  const pending = affiliations.filter(a => a.status === 'pending');
  const active = affiliations.filter(a => a.status === 'active');
  const other = affiliations.filter(a => !['pending', 'active'].includes(a.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="doctor-affiliations-page">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Stethoscope className="h-5 w-5 text-teal-600" />
            <div>
              <h1 className="text-lg font-bold text-slate-900" data-testid="affiliations-title">Affiliations</h1>
              <p className="text-xs text-slate-500">Manage your provider affiliations</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/doctor/profile')} data-testid="back-to-profile">Profile</Button>
            <Button variant="ghost" size="sm" onClick={() => { localStorage.removeItem('treatx_unified_token'); localStorage.removeItem('treatx_unified_user'); navigate('/doctor/login'); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Pending invitations */}
        {pending.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" /> Pending Invitations ({pending.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pending.map(aff => (
                <div key={aff.affiliation_id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`pending-${aff.affiliation_id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{aff.provider_name || aff.provider_id}</p>
                    <p className="text-xs text-slate-500">Role: {aff.role} | Visibility: {aff.visibility}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8" onClick={() => handleAccept(aff.affiliation_id)} disabled={actionLoading === aff.affiliation_id} data-testid={`accept-${aff.affiliation_id}`}>
                      {actionLoading === aff.affiliation_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />} Accept
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleReject(aff.affiliation_id)} disabled={actionLoading === aff.affiliation_id} data-testid={`reject-${aff.affiliation_id}`}>
                      <XCircle className="h-3 w-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Active affiliations */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-emerald-600" /> Active Affiliations ({active.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {active.length > 0 ? (
              <div className="space-y-2" data-testid="active-affiliations">
                {active.map(aff => (
                  <div key={aff.affiliation_id} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid={`active-${aff.affiliation_id}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{aff.provider_name || aff.provider_id}</p>
                      <p className="text-xs text-slate-500">Role: {aff.role} | Visibility: {aff.visibility}</p>
                    </div>
                    <Badge className={STATUS_STYLES.active}>Active</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400" data-testid="no-active-affiliations">No active affiliations yet</p>
            )}
          </CardContent>
        </Card>

        {/* Past affiliations */}
        {other.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-500">Past ({other.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {other.map(aff => (
                  <div key={aff.affiliation_id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3" data-testid={`past-${aff.affiliation_id}`}>
                    <div>
                      <p className="text-sm text-slate-600">{aff.provider_name || aff.provider_id}</p>
                      <p className="text-xs text-slate-400">Role: {aff.role}</p>
                    </div>
                    <Badge className={STATUS_STYLES[aff.status] || STATUS_STYLES.revoked}>{aff.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
